/**
 * One library item's words as the shelf shows them: the owner's rules, the parts, and each part's pieces (a
 * paragraph, a list line, a heading or a table). A piece is changed in place in the raw text, so every other
 * line keeps its exact words. "In short" is built from the first paragraph of each part, so the summary and
 * the parts cannot disagree; an older item with its own "In short" keeps it.
 */

export type BlockType = "p" | "li" | "ol" | "table" | "h";

export interface Block {
  t: BlockType;
  /** 1 for a list line indented under another. */
  lvl: 0 | 1;
  /** An ordered line's own number. */
  num?: number;
  text: string;
  /** The block's lines in the item's raw text: [from, to). */
  from: number;
  to: number;
}

export interface Part {
  id: string;
  title: string;
  blocks: Block[];
}

export interface Doc {
  title: string;
  /** The owner's own rules, in their words: they win over anything below. */
  rules: Block[];
  /** An older item's own "In short" lines; null when the summary is built from the parts. */
  written: Block[] | null;
  parts: Part[];
}

export const RULES_TITLE = "Your rules";
/** The owner's rules, where a piece or a change names its part. */
export const RULES_ID = "your-rules";
const RULES_INTRO = "The owner's own rules. They win over anything below.";

/** Where a piece is: a part's block, the owner's rules, or a new line after a block. */
export type Spot = { part: string; bi: number; fresh?: boolean };

const slug = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
export const norm = (s: string) => s.replace(/\s+/g, " ").trim();
const plainTitle = (h: string) => h.replace(/^\d+[a-z]?\.\s*/i, "").trim();

const lines = (body: string) => body.replace(/\r\n/g, "\n").split("\n");

/** The blocks in lines [from, to) of the raw text. */
function blocksIn(all: string[], from: number, to: number): Block[] {
  const out: Block[] = [];
  let cur: Block | null = null;
  for (let i = from; i < to; i++) {
    const line = all[i].replace(/\s+$/, "");
    let m: RegExpMatchArray | null;
    if (!line.trim()) {
      cur = null;
      continue;
    }
    if ((m = line.match(/^(\s*)[-*] (.*)$/))) {
      cur = { t: "li", lvl: m[1].length >= 2 ? 1 : 0, text: m[2], from: i, to: i + 1 };
      out.push(cur);
      continue;
    }
    if ((m = line.match(/^(\s*)(\d+)\. (.*)$/))) {
      cur = { t: "ol", lvl: m[1].length >= 2 ? 1 : 0, num: Number(m[2]), text: m[3], from: i, to: i + 1 };
      out.push(cur);
      continue;
    }
    if (/^\s*\|/.test(line)) {
      if (cur?.t === "table") {
        cur.text += `\n${line}`;
        cur.to = i + 1;
      } else {
        cur = { t: "table", lvl: 0, text: line, from: i, to: i + 1 };
        out.push(cur);
      }
      continue;
    }
    if ((m = line.match(/^#{3,4} (.*)$/))) {
      out.push({ t: "h", lvl: 0, text: m[1], from: i, to: i + 1 });
      cur = null;
      continue;
    }
    if (cur && (cur.t === "li" || cur.t === "ol") && /^\s+\S/.test(line)) {
      cur.text += ` ${line.trim()}`;
      cur.to = i + 1;
      continue;
    }
    if (cur?.t === "p") {
      cur.text += ` ${line.trim()}`;
      cur.to = i + 1;
      continue;
    }
    cur = { t: "p", lvl: 0, text: line.trim(), from: i, to: i + 1 };
    out.push(cur);
  }
  return out;
}

/** Where each "## " section starts, and the title line. */
function layout(all: string[]) {
  const title = all.findIndex((l) => l.startsWith("# "));
  const heads = all.map((l, i) => (l.startsWith("## ") ? i : -1)).filter((i) => i >= 0);
  return { title, heads };
}

export function parseDoc(body: string): Doc {
  const all = lines(body);
  const { title, heads } = layout(all);
  const doc: Doc = { title: title >= 0 ? all[title].replace(/^# /, "").trim() : "", rules: [], written: null, parts: [] };
  const introFrom = title + 1;
  const introTo = heads[0] ?? all.length;
  const intro = blocksIn(all, introFrom, introTo);
  if (intro.length) doc.parts.push({ id: "brief", title: "The brief", blocks: intro });
  heads.forEach((h, k) => {
    const head = all[h].replace(/^## /, "").trim();
    const blocks = blocksIn(all, h + 1, heads[k + 1] ?? all.length);
    if (head === RULES_TITLE) doc.rules = blocks.filter((b) => b.t === "li");
    else if (/^In short$/i.test(head)) doc.written = blocks.filter((b) => b.t === "li" || b.t === "p");
    else doc.parts.push({ id: slug(plainTitle(head)), title: plainTitle(head), blocks });
  });
  return doc;
}

/** Each part's first paragraph: the line "In short" shows for it. */
export const leadOf = (p: Part): Block | null => (p.blocks[0]?.t === "p" ? p.blocks[0] : null);

/** The summary: an older item's own lines, or one line per part. */
export function summaryOf(doc: Doc): { part: Part | null; text: string }[] {
  if (doc.written) return doc.written.map((b) => ({ part: null, text: b.text }));
  return doc.parts.map((p) => ({ part: p, text: leadOf(p)?.text ?? "" })).filter((x) => x.text);
}

/** Every piece's words, to mark the ones the owner wrote. */
export const pieceSet = (doc: Doc) => new Set(doc.parts.flatMap((p) => p.blocks.map((b) => norm(b.text))));

// --- Changing the words ---

function asLines(b: Pick<Block, "t" | "lvl" | "num">, value: string): string[] {
  const v = value.trim();
  if (!v) return [];
  const pad = b.lvl ? "  " : "";
  if (b.t === "li") return v.split(/\n+/).map((l) => `${pad}- ${l.replace(/^\s*[-*]\s+/, "").trim()}`);
  if (b.t === "ol") return [`${pad}${b.num ?? 1}. ${v.replace(/\s*\n\s*/g, " ")}`];
  if (b.t === "h") return [`### ${v.replace(/\s*\n\s*/g, " ")}`];
  return v.split("\n");
}

const join = (all: string[]) => `${all.join("\n").replace(/\n{3,}/g, "\n\n").trim()}\n`;

/** The text with one piece changed; emptying it takes it out. */
export function setPiece(body: string, b: Block, value: string): string {
  const all = lines(body);
  all.splice(b.from, b.to - b.from, ...asLines(b, value));
  return join(all);
}

/** The text with a new list line after `after`, of the same kind. Nothing changes for an empty line. */
export function addAfter(body: string, after: Block, value: string): string {
  if (!value.trim()) return body;
  const all = lines(body);
  const next = after.t === "ol" ? { ...after, num: (after.num ?? 0) + 1 } : after.t === "li" ? after : { t: "li" as const, lvl: 0 as const };
  all.splice(after.to, 0, ...asLines(next, value));
  return join(all);
}

/** The text with the owner's rules set: a "Your rules" part before the first part, gone when there are none. */
export function setRules(body: string, rules: string[]): string {
  const all = lines(body);
  const { title, heads } = layout(all);
  const at = heads.findIndex((h) => all[h].replace(/^## /, "").trim() === RULES_TITLE);
  if (at >= 0) all.splice(heads[at], (heads[at + 1] ?? all.length) - heads[at]);
  const keep = rules.map((r) => norm(r)).filter(Boolean);
  if (keep.length) {
    const first = layout(all).heads[0] ?? all.length;
    const where = Math.max(first, title + 1);
    all.splice(where, 0, `## ${RULES_TITLE}`, RULES_INTRO, "", ...keep.map((r) => `- ${r}`), "");
  }
  return join(all);
}

/**
 * The words once one piece is changed, with the note its version gets, and whether it was the line In short
 * shows; null when nothing changes. An empty line or rule is taken out.
 */
export function changeOf(body: string, doc: Doc, s: Spot, value: string): { body: string; note: string; lead: boolean } | null {
  if (s.part === RULES_ID) {
    const rules = doc.rules.map((r) => r.text);
    if (s.fresh) rules.splice(s.bi + 1, 0, value);
    else rules[s.bi] = value;
    const was = s.fresh ? "" : (doc.rules[s.bi]?.text ?? "");
    if (norm(value) === norm(was)) return null;
    return { body: setRules(body, rules), note: value.trim() ? (s.fresh ? `Added a rule: ${norm(value)}` : `Changed a rule: ${norm(value)}`) : "Took out a rule", lead: false };
  }
  const part = doc.parts.find((p) => p.id === s.part);
  const b = part?.blocks[s.bi];
  if (!part || !b) return null;
  if (s.fresh) return value.trim() ? { body: addAfter(body, b, value), note: `Added a line to ${part.title}`, lead: false } : null;
  if (norm(value) === norm(b.text)) return null;
  const lead = leadOf(part) === b && !doc.written;
  return {
    body: setPiece(body, b, value),
    note: lead ? `Changed the first line of ${part.title}` : value.trim() ? `Changed a line in ${part.title}` : `Took a line out of ${part.title}`,
    lead,
  };
}

// --- Your rule wins here ---

const STOP = new Set(
  "about after again agent always before being could every from goal goals have into just more most never only other over should their there these they this those through under until when where which while will with without would your yours ask asking change changes".split(
    " ",
  ),
);
const keywords = (s: string) =>
  new Set(
    norm(s)
      .toLowerCase()
      .split(/[^a-z0-9$%]+/)
      .filter((w) => w.length >= 5 && !STOP.has(w))
      .map((w) => w.replace(/(ing|ed|es|s)$/, "")),
  );

/**
 * The parts each rule overrides: the parts whose title or first line share a meaningful word with the rule
 * ("daily budget" and "Budget and learning"). Why: the owner sees where a rule takes over without reading the
 * detail; the agent is told the rules win wherever they apply.
 */
export function ruleWins(doc: Doc): Record<string, number[]> {
  const out: Record<string, number[]> = {};
  doc.rules.forEach((r, ri) => {
    const rk = keywords(r.text);
    if (!rk.size) return;
    for (const p of doc.parts) {
      const pk = keywords(`${p.title} ${leadOf(p)?.text ?? ""}`);
      if ([...rk].some((w) => pk.has(w))) (out[p.id] ??= []).push(ri);
    }
  });
  return out;
}

// --- What changed: parts matched by name, lines by their longest common run, words inside a changed line ---

function lcs<T>(A: T[], B: T[], eq: (a: T, b: T) => boolean): Int32Array[] {
  const dp = Array.from({ length: A.length + 1 }, () => new Int32Array(B.length + 1));
  for (let i = A.length - 1; i >= 0; i--) for (let j = B.length - 1; j >= 0; j--) dp[i][j] = eq(A[i], B[j]) ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  return dp;
}

const words = (s: string) => norm(s).split(" ").filter(Boolean);
function likeness(a: string, b: string): number {
  const A = words(a);
  const B = words(b);
  if (!A.length || !B.length) return 0;
  return (2 * lcs(A, B, (x, y) => x === y)[0][0]) / (A.length + B.length);
}

export type LineChange = { k: "add"; b: Block } | { k: "rm"; a: Block } | { k: "mod"; a: Block; b: Block };

function lineChanges(A: Block[], B: Block[]): LineChange[] {
  const eq = (x: Block, y: Block) => norm(x.text) === norm(y.text);
  const dp = lcs(A, B, eq);
  const ops: ({ k: "same" } | { k: "rm"; a: Block } | { k: "add"; b: Block })[] = [];
  let i = 0;
  let j = 0;
  while (i < A.length && j < B.length) {
    if (eq(A[i], B[j])) {
      ops.push({ k: "same" });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) ops.push({ k: "rm", a: A[i++] });
    else ops.push({ k: "add", b: B[j++] });
  }
  while (i < A.length) ops.push({ k: "rm", a: A[i++] });
  while (j < B.length) ops.push({ k: "add", b: B[j++] });
  const out: LineChange[] = [];
  for (let k = 0; k < ops.length; ) {
    if (ops[k].k === "same") {
      k++;
      continue;
    }
    const rms: Block[] = [];
    const adds: Block[] = [];
    while (k < ops.length && ops[k].k !== "same") {
      const o = ops[k++];
      if (o.k === "rm") rms.push(o.a);
      else if (o.k === "add") adds.push(o.b);
    }
    const used = new Set<number>();
    const paired = adds.map((b) => {
      let best = -1;
      let score = 0;
      rms.forEach((a, ri) => {
        if (used.has(ri)) return;
        const s = likeness(a.text, b.text);
        if (s > score) {
          score = s;
          best = ri;
        }
      });
      if (best >= 0 && score >= 0.4) {
        used.add(best);
        return { k: "mod" as const, a: rms[best], b };
      }
      return { k: "add" as const, b };
    });
    rms.forEach((a, ri) => used.has(ri) || out.push({ k: "rm", a }));
    out.push(...paired);
  }
  return out;
}

export interface PartChange {
  id: string;
  title: string;
  status: "added" | "removed" | "";
  changes: LineChange[];
}

/** Part by part, what changed from `older` to `newer`. The owner's rules count as a part. */
export function compareDocs(older: Doc, newer: Doc): { parts: PartChange[]; same: number } {
  const withRules = (d: Doc): Part[] => (d.rules.length ? [{ id: RULES_ID, title: RULES_TITLE, blocks: d.rules }, ...d.parts] : d.parts);
  const A = withRules(older);
  const B = withRules(newer);
  const ids = B.map((p) => p.id);
  A.forEach((p, i) => ids.includes(p.id) || ids.splice(Math.min(i, ids.length), 0, p.id));
  const parts: PartChange[] = [];
  let same = 0;
  for (const id of ids) {
    const a = A.find((p) => p.id === id);
    const b = B.find((p) => p.id === id);
    const changes = lineChanges(a?.blocks ?? [], b?.blocks ?? []);
    if (changes.length) parts.push({ id, title: (b ?? a)!.title, status: !a ? "added" : !b ? "removed" : "", changes });
    else same++;
  }
  return { parts, same };
}

export type WordRun = { k: "same" | "del" | "ins"; t: string };

/** The words of a changed line: what stayed, what was taken out and what was added. */
export function wordRuns(a: string, b: string): WordRun[] {
  const toks = (s: string) => norm(s).match(/\s+|[\p{L}\p{N}_$%'’/@#&+=-]+|[^\s\p{L}\p{N}]/gu) ?? [];
  const A = toks(a);
  const B = toks(b);
  const dp = lcs(A, B, (x, y) => x === y);
  const seq: WordRun[] = [];
  let i = 0;
  let j = 0;
  const push = (k: WordRun["k"], t: string) => {
    const last = seq[seq.length - 1];
    if (last && last.k === k) last.t += t;
    else seq.push({ k, t });
  };
  while (i < A.length && j < B.length) {
    if (A[i] === B[j]) {
      push("same", A[i]);
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) push("del", A[i++]);
    else push("ins", B[j++]);
  }
  while (i < A.length) push("del", A[i++]);
  while (j < B.length) push("ins", B[j++]);
  return seq;
}
